import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react'
import { cloneElement, isValidElement, useMemo, useRef, useState } from 'react'

import type { TooltipProps } from './tooltip.types'

import { useTh, useUp, v } from '../core'
import { Link } from '../link'
import { useModalContext } from '../modal/context'
import { zIndices } from '../theme'
import { isString } from '../utils'
import { bg } from './consts'
import { TooltipContent } from './tooltipContent'

const stripHtmlTags = (html: string): string => {
  const withoutTags = html.replace(/<[^>]*>/g, '')
  // Decode HTML entities
  const textarea = document.createElement('textarea')
  textarea.innerHTML = withoutTags
  return textarea.value
}

/**
 * A simple text popup hint.
 *
 * Displayed on mouse enter, and is hidden on mouse leave.
 *
 * */
export const Tooltip = (props: TooltipProps) => {
  const {
    children,
    fontSize = '16px',
    linkText,
    linkProps,
    minWidth,
    maxWidth = '400px',
    text = '',
    placement = 'top',
    disabled = false,
    visible = false,
    offset: offsetProp = [0, 0],
    whiteSpace = 'pre-wrap',
    wordBreak = 'break-word',
    portalRoot = undefined,
  } = props

  const isDesktop = useUp('md')
  const [isOpen, setIsOpen] = useState(visible)
  const arrowRef = useRef(null)

  const middleware = useMemo(
    () => [
      offset({ mainAxis: offsetProp[1] + 6, crossAxis: offsetProp[0] }), // +6 for arrow
      flip(),
      shift(),
      arrow({ element: arrowRef }),
    ],

    [offsetProp[0], offsetProp[1]],
  )

  const { refs, floatingStyles, context } = useFloating({
    open: visible || isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  })
  // Opacity-only transition - no position movement
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
  })
  const hover = useHover(context, { move: false, handleClose: safePolygon() })
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role])

  const darkBlue18 = useTh(`colors.${bg.regular}`)
  const sandstone55 = useTh(`colors.${bg.disabled}`)
  const arrowColor = disabled ? sandstone55 : darkBlue18

  const strippedText = useMemo(() => (isString(text) ? stripHtmlTags(text) : ''), [text])

  // Get parent modal context to determine if tooltip is inside a modal
  const modalContext = useModalContext()
  // If inside a modal, use popover z-index + offset; otherwise use standard tooltip z-index
  const zIndex = modalContext?.zIndex ? zIndices.popover + 10 : zIndices.tooltip

  const content = isString(text) ? (
    <TooltipContent
      disabled={disabled}
      fontSize={fontSize}
      maxWidth={maxWidth}
      minWidth={minWidth}
      whiteSpace={whiteSpace}
      wordBreak={wordBreak}
    >
      <v.div>
        {strippedText}
        {!!linkText && (
          <>
            &nbsp;
            <Link variant="white" {...linkProps}>
              {linkText}
            </Link>
          </>
        )}
      </v.div>
    </TooltipContent>
  ) : (
    <TooltipContent disabled={disabled} fontSize={fontSize} maxWidth={maxWidth} minWidth={minWidth}>
      {text}
    </TooltipContent>
  )

  // Clone the child element to add ref and props.
  // Compose refs so any existing ref on the child is preserved alongside the floating reference ref.
  const childRef = isValidElement(children) ? ((children as any).ref ?? (children as any).props?.ref) : null
  const composedRef =
    childRef != null
      ? (el: Element | null) => {
          refs.setReference(el)
          if (typeof childRef === 'function') childRef(el)
          else if (childRef && typeof childRef === 'object') (childRef as React.MutableRefObject<unknown>).current = el
        }
      : refs.setReference
  const childElement = isValidElement(children)
    ? cloneElement(children as React.ReactElement<any>, {
        ref: composedRef,
        ...getReferenceProps(),
      })
    : children

  return (
    <>
      {childElement}
      {isMounted && (
        <FloatingPortal key={portalRoot ? 'custom' : 'default'} root={portalRoot ?? undefined}>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...transitionStyles, zIndex }}
            {...getFloatingProps()}
          >
            {content}
            {isDesktop && <FloatingArrow ref={arrowRef} context={context} fill={arrowColor} width={12} height={6} />}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

Tooltip.displayName = 'Tooltip'

export default Tooltip
